import { Field, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import {
  AutoCompleteSelectUI,
  DateTimePickerUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
  PhoneInputUI,
} from '../../../Interface';
import { IconButton, Stack, Grid, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import companyService from '../../../../service/company.service';
import { generateAutocompleteItems } from '../../../../utils/helper';
import CompanyContactAddFields from '../../../Company/CompanyContactAddFields';

const TodoDialogFields = ({ toggleField, visibleFields, autoCompleteOptions, isEdit }) => {
  const { errors, validateField, setFieldValue, values } = useFormikContext();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyContacts, setCompanyContacts] = useState([]);

  const defCompanyContactAdd = {
    name: '',
    role: '',
    phoneNumber: '',
    email: '',
  };
  const [addContactMode, setAddContactMode] = useState(false);
  const [newContact, setNewContact] = useState(defCompanyContactAdd);

  const simpleTaskRespPersonListVal = autoCompleteOptions.user.filter((option) =>
    values.simpleTaskRespPersonlist.some((person) => person.userId === option.value)
  );

  const updateDuration = (startDate, endDate) => {
    const duration = dayjs(endDate).diff(dayjs(startDate), 'hour', true);

    setFieldValue('duration', duration);
  };

  const getCompanyData = async (companyId) => {
    const selCom = await companyService.getCompanyById(companyId);
    setSelectedCompany(selCom);
  };

  useEffect(() => {
    if (values.companyId) {
      setCompanyContacts([]);
      getCompanyData(values.companyId);
    } else {
      setSelectedCompany(null);
      setCompanyContacts([]);
    }
  }, [values.companyId]);

  useEffect(() => {
    if (!selectedCompany) {
      setFieldValue('companyContactId', null);
      return;
    }

    setCompanyContacts(generateAutocompleteItems(selectedCompany.companyContact, 'name', 'id'));

    if (!isEdit && !values.companyContactId) {
      const defContact = selectedCompany.companyContact.find((cc) => cc.default);
      setFieldValue('companyContactId', defContact?.id ?? selectedCompany?.companyContact[0]?.id ?? null);
    }
  }, [selectedCompany]);

  const handleAddContact = async () => {
    const data = {
      ...newContact,
      role: newContact.role || 'Kapcsolattartó',
    };

    const contact = await companyService.createCompanyContact(values.companyId, data);

    await setFieldValue('companyContactId', contact.id);

    setSelectedCompany((prev) => ({
      ...prev,
      companyContact: [...prev.companyContact, contact],
    }));

    resetContactAdd();
  };

  const resetContactAdd = () => {
    setAddContactMode(false);
    setNewContact(defCompanyContactAdd);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Field
          type="text"
          name="simpleTaskName"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Mikor, kivel, mit és MIÉRT kell tenned legközelebb? Írd ide a feladat célját!" />}
          helperText={<FormErrorUI message={errors.simpleTaskName} />}
          InputLabelProps={{ shrink: values.simpleTaskName !== '' }}
          inputProps={{
            maxLength: 300,
          }}
          multiline
          rows={4}
        />
      </Grid>
      <Grid item xs={6}>
        <Field
          type="autocomplete"
          name="resposiblePersonId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Felelős" />}
          size="small"
          variant="outlined"
          helperText={<FormErrorUI message={errors.resposiblePersonId} />}
          onChange={(_e, newVal) => {
            setFieldValue('resposiblePersonId', newVal?.value ?? null).then(() => {
              validateField('resposiblePersonId');
            });
          }}
          InputLabelProps={{ shrink: values.resposiblePersonId !== '' }}
          selectedValue={values.resposiblePersonId}
          dataset={autoCompleteOptions.user}
        />
      </Grid>

      <Grid item xs={6}>
        <Field
          type="autocomplete"
          name="simpleTaskTypeId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Feladat típusa" />}
          variant="outlined"
          size="small"
          helperText={<FormErrorUI message={errors.simpleTaskTypeId} />}
          onChange={(_e, newVal) => {
            setFieldValue('simpleTaskTypeId', newVal?.value ?? null).then(() => {
              validateField('simpleTaskTypeId');
            });
          }}
          InputLabelProps={{ shrink: values.simpleTaskTypeId !== '' }}
          selectedValue={values.simpleTaskTypeId}
          dataset={autoCompleteOptions?.taskType}
        />
      </Grid>

      <Grid item xs={6}>
        <Field
          type="date"
          name="startDate"
          component={DateTimePickerUI}
          value={values.startDate}
          fullWidth
          label={<FormLabelUI text="Kezdő dátum" />}
          variant="outlined"
          size="small"
          helperText={<FormErrorUI message={errors.startDate} />}
          onChange={(date) => {
            setFieldValue('startDate', date).then(() => {
              validateField('startDate');
            });

            updateDuration(date, values.endDate);
          }}
          InputLabelProps={{ shrink: values.startDate !== '' }}
        />
      </Grid>

      <Grid item xs={6}>
        <Field
          type="date"
          name="endDate"
          component={DateTimePickerUI}
          value={values.endDate}
          fullWidth
          label={<FormLabelUI text="Határidő" />}
          variant="outlined"
          size="small"
          helperText={<FormErrorUI message={errors.endDate} />}
          onChange={(date) => {
            setFieldValue('endDate', date).then(() => {
              validateField('endDate');
            });

            updateDuration(values.startDate, date);
          }}
          InputLabelProps={{ shrink: values.endDate !== '' }}
        />
      </Grid>

      <Grid item xs={6}>
        <Field
          type="autocomplete"
          name="companyId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Cég" />}
          variant="outlined"
          size="small"
          helperText={<FormErrorUI message={errors.companyId} />}
          onChange={(_e, newVal) => {
            setFieldValue('companyId', newVal?.value ?? null).then(() => {
              validateField('companyId');
            });
          }}
          InputLabelProps={{ shrink: values.companyId !== '' }}
          selectedValue={values.companyId}
          dataset={autoCompleteOptions?.company}
        />
      </Grid>
      {addContactMode ? (
        <Grid item xs={12}>
          <Box sx={{ p: 1, borderRadius: 1, bgcolor: '#E7F3FD' }}>
            <CompanyContactAddFields
              newContactState={{ newContact, setNewContact }}
              resetContactAdd={resetContactAdd}
              onAdd={handleAddContact}
              variant="outlined"
              size="small"
              className="md:grid-cols-2"
            />
          </Box>
        </Grid>
      ) : (
        <>
          <Grid item xs={6}>
            <Field
              type="autocomplete"
              name="companyContactId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Kapcsolattartó" />}
              variant="outlined"
              size="small"
              helperText={<FormErrorUI message={errors.companyContactId} />}
              onChange={(_e, newVal) => {
                if (newVal?.createOption) {
                  setNewContact({
                    name: newVal.inputValue,
                    role: '',
                    phoneNumber: '',
                    email: '',
                  });

                  setAddContactMode(true);
                  return;
                }

                setFieldValue('companyContactId', newVal?.value ?? null).then(() => {
                  validateField('companyContactId');
                });
              }}
              createOptionLabel={(inputValue) => `"${inputValue}" hozzáadása új kapcsolattartóként`}
              disabled={!values.companyId}
              InputLabelProps={{ shrink: values.companyContactId !== '' }}
              selectedValue={companyContacts.length > 0 ? values.companyContactId : null}
              selectedLabelValue={
                companyContacts.length > 0
                  ? companyContacts.find((cc) => cc.value === values.companyContactId)?.label
                  : ''
              }
              dataset={companyContacts}
            />
          </Grid>

          <Grid item xs={6}>
            <PhoneInputUI
              fullWidth
              label={<FormLabelUI text="Kapcsolattartó telefonszáma" />}
              variant="outlined"
              size="small"
              value={
                selectedCompany?.companyContact?.find((cc) => cc.id === values.companyContactId)?.phoneNumber ?? ''
              }
              disabled
            />
          </Grid>

          <Grid item xs={6}>
            <InputUI
              fullWidth
              label={<FormLabelUI text="Kapcsolattartó email címe" />}
              variant="outlined"
              size="small"
              value={selectedCompany?.companyContact?.find((cc) => cc.id === values.companyContactId)?.email ?? ''}
              disabled
            />
          </Grid>
        </>
      )}

      {visibleFields.simpleTaskRespPersonlist && (
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center">
            <Field
              type="autocomplete"
              name="simpleTaskRespPersonlist"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Érintettek" />}
              variant="outlined"
              size="small"
              helperText={<FormErrorUI message={errors.simpleTaskRespPersonlist} />}
              onChange={(_e, newVal) => {
                const mappedValues = newVal.map((item) => ({
                  userId: item.value,
                  roleName: 'Érintett',
                }));

                setFieldValue('simpleTaskRespPersonlist', mappedValues).then(() => {
                  validateField('simpleTaskRespPersonlist');
                });
              }}
              InputLabelProps={{ shrink: values.simpleTaskRespPersonlist !== '' }}
              selectedValue={simpleTaskRespPersonListVal}
              dataset={autoCompleteOptions?.user}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              multiple
              freeSolo
            />
            <IconButton
              onClick={() => toggleField('simpleTaskRespPersonlist', values)}
              sx={{ color: '#9ca3af', mt: -0.5 }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Grid>
      )}
    </Grid>
  );
};

export default TodoDialogFields;
